
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

/* ---- DATA SAME AS YOURS ---- */
const gmvByRegion = [
  { region: "North", gmv: 450000 },
  { region: "South", gmv: 380000 },
  { region: "East", gmv: 520000 },
  { region: "West", gmv: 410000 },
  { region: "Central", gmv: 350000 },
];

const gmvByDistributor = [
  { name: "Alpha Distributors", gmv: 280000 },
  { name: "Beta Supply Co", gmv: 340000 },
  { name: "Gamma Traders", gmv: 220000 },
  { name: "Delta Partners", gmv: 390000 },
  { name: "Epsilon Ltd", gmv: 180000 },
];

const gmvBySku = [
  { sku: "SKU-001", product: "Premium Bricks", gmv: 180000 },
  { sku: "SKU-002", product: "Standard Blocks", gmv: 250000 },
  { sku: "SKU-003", product: "Cement Mix", gmv: 320000 },
  { sku: "SKU-004", product: "Roofing Tiles", gmv: 210000 },
  { sku: "SKU-005", product: "Wall Panels", gmv: 150000 },
];

const monthlyGmv = [
  { month: "Jan", gmv: 420000, target: 400000 },
  { month: "Feb", gmv: 450000, target: 420000 },
  { month: "Mar", gmv: 480000, target: 440000 },
  { month: "Apr", gmv: 520000, target: 460000 },
  { month: "May", gmv: 490000, target: 480000 },
  { month: "Jun", gmv: 550000, target: 500000 },
];

const brandComparison = [
  { month: "Jan", yourBrand: 420000, brandA: 380000, brandB: 450000 },
  { month: "Feb", yourBrand: 450000, brandA: 395000, brandB: 465000 },
  { month: "Mar", yourBrand: 480000, brandA: 410000, brandB: 475000 },
  { month: "Apr", yourBrand: 520000, brandA: 425000, brandB: 490000 },
  { month: "May", yourBrand: 490000, brandA: 440000, brandB: 505000 },
  { month: "Jun", yourBrand: 550000, brandA: 455000, brandB: 520000 },
];

const brandRanking = [
  { brand: "Your Brand", gmv: 2910000, rank: 2, growth: "+18%" },
  { brand: "Brand B", gmv: 2905000, rank: 1, growth: "+15%" },
  { brand: "Brand C", gmv: 2660000, rank: 3, growth: "+12%" },
];



const Card = ({ children, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-lg shadow p-4 hover:shadow-md cursor-pointer"
  >
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`mb-2 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-sm font-medium">{children}</h3>
);

const CardContent = ({ children }) => <div>{children}</div>;

export default function GmvMetricsSection() {
    const [timeRange, setTimeRange] = useState("ytd");

  return (
    <div className="space-y-2 md:space-y-8">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg md:text-2xl font-bold">GMV Analytics</h2>
        <select className="border rounded-md px-3 py-2 text-sm">
          <option>2026</option>
          <option>2025</option>
          <option>2024</option>
        </select>
      </div>

      {/* METRIC CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[
          ["Total GMV (This Month)", "$550,000", "↑ 12% from last month"],
          ["GMV YTD", "$2.91M", "↑ 18% from last year"],
          ["Avg Order Value", "$3,526", "↑ 5% from last month"],
          ["Active Distributors", "24", "Across 5 regions"],
        ].map(([title, value, sub], i) => (
          <div key={i} className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-lg md:text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-green-600 mt-1">{sub}</p>
          </div>
        ))}
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-semibold mb-4">GMV Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyGmv}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line dataKey="gmv" stroke="#2563eb" />
              <Line dataKey="target" stroke="#94a3b8" strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-semibold mb-4">GMV by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={gmvByRegion}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="region" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="gmv" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>


      <Card>
  <CardHeader>
  <div className="flex items-center justify-between w-full">
    <div>
      <CardTitle>GMV Brand Comparison</CardTitle>
      <p className="text-sm text-gray-500 mt-1">
        Compare your brand&apos;s performance with competitors
      </p>
    </div>

    <select
      value={timeRange}
      onChange={(e) => setTimeRange(e.target.value)}
      className="w-32 px-3 py-2 text-sm border rounded-md bg-white"
    >
      <option value="ytd">YTD 2026</option>
      <option value="6months">Last 6 Months</option>
      <option value="3months">Last 3 Months</option>
    </select>
  </div>
</CardHeader>

  <CardContent>
    <div className="w-full h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={brandComparison}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip
            formatter={(value) =>
              `$${Number(value).toLocaleString()}`
            }
          />
          <Legend />

          <Line
            type="monotone"
            dataKey="yourBrand"
            stroke="#3b82f6"
            strokeWidth={3}
            name="Your Brand"
          />
          <Line
            type="monotone"
            dataKey="brandA"
            stroke="#94a3b8"
            strokeWidth={2}
            name="Brand A"
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey="brandB"
            stroke="#f59e0b"
            strokeWidth={2}
            name="Brand B"
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey="brandC"
            stroke="#10b981"
            strokeWidth={2}
            name="Brand C"
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey="brandD"
            stroke="#8b5cf6"
            strokeWidth={2}
            name="Brand D"
            strokeDasharray="5 5"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </CardContent>
</Card>


      {/* BRAND RANKING TABLE */}
      <Card>
  <CardHeader>
    <CardTitle>Brand Performance Ranking (YTD)</CardTitle>
  </CardHeader>

  <CardContent>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b">
            <th className="text-left py-3 px-4 font-medium">Rank</th>
            <th className="text-left py-3 px-4 font-medium">Brand</th>
            <th className="text-left py-3 px-4 font-medium">
              Total GMV (YTD)
            </th>
            <th className="text-left py-3 px-4 font-medium">
              YoY Growth
            </th>
            <th className="text-left py-3 px-4 font-medium">
              Market Share
            </th>
          </tr>
        </thead>

        <tbody>
          {brandRanking.map((item, index) => (
            <tr
              key={index}
              className={`border-b hover:bg-gray-50 ${
                item.brand === "Your Brand"
                  ? "bg-blue-50 font-semibold"
                  : ""
              }`}
            >
              {/* Rank */}
              <td className="py-3 px-4">
                {item.rank === 1 && "🥇"}
                {item.rank === 2 && "🥈"}
                {item.rank === 3 && "🥉"}
                {item.rank > 3 && `#${item.rank}`}
              </td>

              {/* Brand */}
              <td className="py-3 px-4 flex items-center gap-2">
                <span>{item.brand}</span>
                {item.brand === "Your Brand" && (
                  <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                    You
                  </span>
                )}
              </td>

              {/* GMV */}
              <td className="py-3 px-4">
                ${item.gmv.toLocaleString()}
              </td>

              {/* Growth */}
              <td className="py-3 px-4">
                <span
                  className={
                    parseInt(item.growth) > 15
                      ? "text-green-600"
                      : "text-gray-600"
                  }
                >
                  {item.growth}
                </span>
              </td>

              {/* Market Share */}
              <td className="py-3 px-4">
                {((item.gmv / 13420000) * 100).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* FOOTER NOTE */}
    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
      <p className="text-sm text-gray-600">
        <strong>Note:</strong> Brand names are anonymized for competitive
        confidentiality. Your brand currently holds{" "}
        <strong>2nd position</strong> with a{" "}
        <strong>21.7% market share</strong>.
      </p>
    </div>
  </CardContent>
</Card>



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
  {/* Top Distributors by GMV */}
  <Card>
    <CardHeader>
      <CardTitle>Top Distributors by GMV</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="w-full h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={gmvByDistributor} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              dataKey="name"
              type="category"
              width={120}
            />
            <Tooltip
              formatter={(value) =>
                `$${Number(value).toLocaleString()}`
              }
            />
            <Bar dataKey="gmv" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </Card>

  {/* Top SKUs by GMV */}
  <Card>
    <CardHeader>
      <CardTitle>Top SKUs by GMV</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="w-full h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={gmvBySku}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="sku" />
            <YAxis />
            <Tooltip
              formatter={(value) =>
                `$${Number(value).toLocaleString()}`
              }
            />
            <Bar dataKey="gmv" fill="#8b5cf6" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </Card>
</div>

    </div>
  );
}
